<template>
  <v-dialog
    :value="value"
    max-width="1180"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="d-flex align-start flex-nowrap">
        <div class="flex-grow-1">
          <div class="text-h6">Localização dos endereços</div>
          <div class="text-body-2 texto-secundario">
            Endereços sem localização, com coordenada provisória, longe da via
            ou da base ficam fora dos planejamentos até serem corrigidos.
          </div>
        </div>
        <v-btn icon aria-label="Fechar" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <div class="d-flex flex-wrap align-center mb-3">
          <v-text-field
            v-model="filtro"
            label="Filtrar por cliente, endereço ou cidade"
            prepend-inner-icon="mdi-magnify"
            clearable
            outlined
            dense
            hide-details
            class="filtro-localizacoes mr-4"
          />
          <v-switch
            v-model="todos"
            label="Mostrar também os corretos"
            color="#5e72e4"
            inset
            hide-details
            class="mt-0"
            @change="carregar"
          />
        </div>
        <div class="d-flex flex-wrap mb-3">
          <span
            v-for="r in resumo"
            :key="r.codigo"
            class="resumo-situacao mr-2 mb-2"
            :style="{ color: r.cor, borderColor: r.cor }"
            >{{ r.total }} {{ r.rotulo }}</span
          >
        </div>
        <div v-if="erro" class="mensagem-erro mb-3">{{ erro }}</div>
        <v-data-table
          :headers="cabecalhos"
          :items="filtrados"
          :loading="carregando"
          :items-per-page="15"
          item-key="id"
          no-data-text="Nenhum endereço precisa de ajuste."
          loading-text="Conferindo as localizações..."
        >
          <template #item.endereco="{ item }">
            <div>{{ item.endereco }}</div>
            <div class="text-caption texto-secundario">
              {{ [item.cidade, item.uf].filter(Boolean).join("/") }}
            </div>
          </template>
          <template #item.situacao="{ item }">
            <span
              class="selo-situacao"
              :style="{ color: cor(item), borderColor: cor(item) }"
              >{{ rotulo(item) }}</span
            >
          </template>
          <template #item.acoes="{ item }">
            <v-btn
              small
              text
              class="text-none"
              color="#175cd3"
              @click="ajustar(item)"
            >
              <v-icon left small>mdi-map-marker-radius</v-icon>Ajustar no mapa
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <AjusteLocalizacaoEndereco
      v-model="editando"
      :endereco-id="enderecoId"
      @salvo="atualizar"
    />
  </v-dialog>
</template>

<script>
import DataService from "@/services/ComumDataService";
import AjusteLocalizacaoEndereco, {
  formatarDistancia,
} from "@/components/operacoes/roteiros/AjusteLocalizacaoEndereco";

const SITUACOES = {
  SEM_LOCALIZACAO: { rotulo: "Sem localização", cor: "#b42318" },
  PROVISORIA: { rotulo: "Provisória", cor: "#b42318" },
  FORA_DO_RAIO: { rotulo: "Longe da base", cor: "#b42318" },
  DISTANTE_DA_VIA: { rotulo: "Longe da via", cor: "#b54708" },
  NAO_CONFERIDA: { rotulo: "Não conferida", cor: "#475467" },
  OK: { rotulo: "Correta", cor: "#027a48" },
};

export default {
  name: "LocalizacoesEnderecos",
  components: { AjusteLocalizacaoEndereco },
  props: { value: { type: Boolean, default: false } },
  data() {
    return {
      enderecos: [],
      filtro: "",
      todos: false,
      carregando: false,
      erro: "",
      editando: false,
      enderecoId: null,
      cabecalhos: [
        { text: "Cliente", value: "cliente" },
        { text: "Local", value: "descricao" },
        { text: "Endereço", value: "endereco" },
        { text: "Situação", value: "situacao" },
        { text: "", value: "acoes", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    filtrados() {
      const termo = (this.filtro || "").toLowerCase();
      if (!termo) return this.enderecos;
      return this.enderecos.filter((e) =>
        [e.cliente, e.descricao, e.endereco, e.cidade]
          .join(" ")
          .toLowerCase()
          .includes(termo)
      );
    },
    resumo() {
      return Object.entries(SITUACOES)
        .map(([codigo, s]) => ({
          codigo,
          ...s,
          total: this.enderecos.filter((e) => e.situacao === codigo).length,
        }))
        .filter((r) => r.total)
        .map((r) => ({ ...r, rotulo: r.rotulo.toLowerCase() }));
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(aberto) {
        if (aberto) this.carregar();
      },
    },
  },
  methods: {
    async carregar() {
      this.carregando = true;
      this.erro = "";
      try {
        const { data } = await DataService.get("/roteirizador/localizacoes", {
          params: { somenteProblemas: !this.todos },
        });
        this.enderecos = data;
      } catch (error) {
        this.erro = "Não foi possível carregar os endereços. Tente novamente.";
      } finally {
        this.carregando = false;
      }
    },
    ajustar(item) {
      this.enderecoId = item.id;
      this.editando = true;
    },
    atualizar(salvo) {
      const indice = this.enderecos.findIndex((e) => e.id === salvo.id);
      if (indice < 0) return;
      if (salvo.situacao === "OK" && !this.todos)
        this.enderecos.splice(indice, 1);
      else this.enderecos.splice(indice, 1, salvo);
      this.$emit("alterado", salvo);
    },
    cor(item) {
      return (SITUACOES[item.situacao] || SITUACOES.NAO_CONFERIDA).cor;
    },
    rotulo(item) {
      const s = SITUACOES[item.situacao] || SITUACOES.NAO_CONFERIDA;
      if (
        item.situacao === "DISTANTE_DA_VIA" &&
        item.distanciaViaMetros != null
      )
        return `${s.rotulo} (${formatarDistancia(item.distanciaViaMetros)})`;
      if (item.situacao === "FORA_DO_RAIO" && item.distanciaBaseKm != null)
        return `${s.rotulo} (${formatarDistancia(
          item.distanciaBaseKm * 1000
        )})`;
      return s.rotulo;
    },
  },
};
</script>

<style scoped>
.texto-secundario {
  color: #475467;
}
.filtro-localizacoes {
  max-width: 420px;
}
.resumo-situacao,
.selo-situacao {
  display: inline-block;
  border: 1px solid;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}
.mensagem-erro {
  color: #b42318;
  background: #fef3f2;
  border-radius: 6px;
  padding: 8px 12px;
}
</style>
